import { useMemo } from 'react';

import { Flex, Steps, useBreakpointValue } from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons';

import { StepperProps, StepperVariants } from './stepper.types';

export const Stepper = ({ steps, activeStep, count, ...rest }: StepperProps) => {
    const isCompactVersion = useBreakpointValue({
        base: true,
        lg: false,
    });

    const stepNumberStyles = {
        lineHeight: 1,
        fontSize: {
            base: '2xs',
            lg: 'md',
        },
    };

    const stepIndicatorSize = useBreakpointValue({
        base: '1.7rem',
        lg: '2.5rem',
    });

    const stepVariants = useMemo(
        () => ({
            active: {
                icon: (
                    <Steps.Indicator
                        color={'secondary.solid'}
                        background={'white'}
                        {...stepNumberStyles}
                    />
                ),
                style: {
                    color: 'secondary.solid',
                },
            },
            inactive: {
                icon: (
                    <Steps.Indicator
                        color={'gray.400'}
                        background={'white'}
                        {...stepNumberStyles}
                    />
                ),
                style: {
                    color: 'gray.400',
                },
            },
            complete: {
                icon: (
                    <Steps.Indicator
                        color={'secondary.solid'}
                        background={'secondary.50'}
                        border={'none'}
                    />
                ),
                style: {
                    color: 'secondary.solid',
                },
            },
            pending: {
                icon: (
                    <IconsLinear.Timer1
                        border={'none'}
                        css={{
                            '&, &[data-current][data-current]': {
                                color: 'orange.solid',
                                background: 'orange.50',
                                fontSize: 'lg',
                                p: '10px',
                            },
                        }}
                    />
                ),
                style: {
                    color: 'orange.solid',
                },
            },
        }),
        [],
    );

    const getStepStyles = (index: number, activeStep: number, isStepperComplete: boolean) => {
        if (steps[index].status === 'pending') {
            return stepVariants[StepperVariants.Pending];
        } else if (isStepperComplete) {
            return stepVariants[StepperVariants.Complete];
        } else if (index < activeStep) {
            return stepVariants[StepperVariants.Active];
        } else {
            return stepVariants[StepperVariants.Inactive];
        }
    };

    const isStepperComplete = activeStep === count;

    return (
        <Steps.Root
            step={activeStep - 1}
            gap={{
                base: 0,
                lg: 4,
            }}
            {...rest}
        >
            <Steps.List
                css={{
                    '--steps-thickness': '1px',
                }}
            >
                {steps.map((step, index) => {
                    const { icon, style } = getStepStyles(index, activeStep, isStepperComplete);

                    return (
                        <Steps.Item
                            index={step.id}
                            key={step.id}
                            css={{
                                alignSelf: isCompactVersion ? 'flex-start' : 'center',
                                flex: 1,
                                '&:last-of-type': {
                                    flex: isCompactVersion ? 1 : undefined,
                                },
                            }}
                        >
                            <Flex
                                flex={isCompactVersion ? 1 : '0 0 auto'}
                                alignItems={'center'}
                                gap={{ base: 1, lg: 4 }}
                                direction={{ base: 'column', lg: 'row' }}
                            >
                                <Steps.Indicator
                                    height={stepIndicatorSize}
                                    width={stepIndicatorSize}
                                    fontSize={{ base: '2xs', lg: 'lg' }}
                                    fontWeight={'bold'}
                                    asChild
                                >
                                    {icon}
                                </Steps.Indicator>
                                <Steps.Title
                                    fontWeight={index <= activeStep ? '700' : 'normal'}
                                    color={style.color}
                                    fontSize={{ base: 'xs', lg: 'md' }}
                                    textAlign={'center'}
                                >
                                    {step.title}
                                </Steps.Title>
                            </Flex>
                            <Steps.Separator
                                color={'gray.500'}
                                css={
                                    isCompactVersion
                                        ? {
                                              height: '1px',
                                              background: isStepperComplete
                                                  ? style.color
                                                  : stepVariants.inactive.style.color,
                                              position: 'absolute',
                                              left: '50%',
                                              right: '-50%',
                                              width: `calc(100% - 2 * ${stepIndicatorSize})`,
                                              marginLeft: 'auto',
                                              marginRight: 'auto',
                                              top: `calc(${stepIndicatorSize} / 2)`,
                                          }
                                        : {
                                              flex: '1 1 auto',
                                          }
                                }
                            />
                        </Steps.Item>
                    );
                })}
            </Steps.List>
        </Steps.Root>
    );
};
